import React, { useState } from 'react';
import { LANGUAGE_CODES } from '@techchange/i18n';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	Collapse,
	Form,
	FormGroup,
} from 'reactstrap';

import { DeleteConfirmationModal } from '@techchange/base';
import { FormField, FIELD_TYPES } from '@techchange/components';
import { Button, useLanguageContext, getLanguageOptions } from '@techchange/components';
import { messages } from '../../i18n';

import { RESOURCE_TYPES } from '../../constants';

import { RESOURCE_FORM_FIELDS } from './hooks/useResourceFormData';
import {
	getWidthOptions,
} from './utils/fieldOptions';
import {
	contentBuilderPropTypes,
} from './utils/propTypes';

import './ContentEditor.scss';

const languageOptions = getLanguageOptions();

const propTypes = {

	/**
	 * The unique ID for the content.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The type of content.
	 */
	type: PropTypes.oneOf([
		RESOURCE_TYPES.SLIDE,
		RESOURCE_TYPES.IMAGE,
		RESOURCE_TYPES.VIDEO,
		RESOURCE_TYPES.EMBED,
		RESOURCE_TYPES.MIRO,
	]).isRequired,

	/**
	 * The form state.
	 */
	formData: PropTypes.shape({

		/**
		 * The currently selected language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The width of the content.
		 */
		width: PropTypes.number.isRequired,

		/**
		 * The list of active resource contents.
		 */
		resource_contents: PropTypes.arrayOf(
			contentBuilderPropTypes.resourceContent
		).isRequired,

	}).isRequired,

	/**
	 * The set of actions for updating the form data state.
	 */
	actions: PropTypes.shape({

		/**
		 * Callback function that adds a new resource content
		 * to the form state.
		 */
		addTranslation: PropTypes.func.isRequired,

		/**
		 * Callback function that removes the current
		 * resource content from the form state and
		 * queues to be deleted.
		 */
		deleteTranslation: PropTypes.func.isRequired,

		/**
		 * Callback function to submit the form.
		 */
		submitForm: PropTypes.func.isRequired,

		/**
		 * Callback function to update a form field state.
		 */
		updateField: PropTypes.func.isRequired,

		/**
		 * Callback function to update the "language" field state.
		 */
		updateLanguage: PropTypes.func.isRequired,

	}).isRequired,

	/**
	 * The list of form field components to display.
	 */
	formFields: PropTypes.element,

	/**
	 * The list of form field components to display in the "Show advanced options"
	 * section.
	 */
	advancedFormFields: PropTypes.element,

	/**
	 * Flag indicating if form is currently being submitted.
	 */
	isSubmitting: PropTypes.bool,

	/**
	 * Function to handle cancel of editing the content.
	 */
	onCancel: PropTypes.func.isRequired,

};

/**
 * Form for resource editors. Handles shared logic and UI between
 * all resource editors, including language, width selection, and
 * form submission.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const ContentEditor = (props) => {

	const {
		id,
		type,
		formData,
		actions,
		formFields,
		isSubmitting,
		advancedFormFields,
		onCancel,
	} = props;

	const intl = useIntl();

	const { defaultLanguage } = useLanguageContext();

	const [showAdvancedFields, setShowAdvancedFields] = useState(false);
	const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

	const WIDTH_OPTIONS = getWidthOptions(intl);

	/**
	 * Toggles the visibility of the "advanced" form fields.
	 */
	const handleToggleShowAdvancedFields = () => {
		setShowAdvancedFields((prev) => !prev);
	};

	/**
	 * Handles removing a translation from the form state and closing
	 * the confirmation modal.
	 *
	 * @returns {Object}  Resolved Promise object.
	 */
	const handleDeleteTranslation = () => {
		actions.deleteTranslation();
		setShowDeleteConfirmationModal(false);
		return Promise.resolve();
	};

	// Get the resource content data for the currently selected language
	const activeTranslation = formData.resource_contents
		.find((rContent) => rContent.language === formData.language);

	const showDeleteTranslationButton = formData.language !== defaultLanguage;

	return (
		<>
			<Form
				className={`content-editor content-builder-${type}-editor`}
				onSubmit={actions.submitForm}>
				{activeTranslation && formFields}
				{activeTranslation && (advancedFormFields || showDeleteTranslationButton) &&
					<>
						<Button
							color="link"
							onClick={handleToggleShowAdvancedFields}>
							{showAdvancedFields
								? <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_HIDE_ADVANCED_OPTIONS} />
								: <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_SHOW_ADVANCED_OPTIONS} />
							}
						</Button>
						<Collapse
							className="mt-3"
							isOpen={showAdvancedFields}>
							<>
								{advancedFormFields}
								{showDeleteTranslationButton &&
									<FormGroup>
										<Button
											className="text-danger"
											color="link"
											onClick={() => setShowDeleteConfirmationModal(true)}>
											<i
												className="fa-regular fa-trash me-1"
												aria-hidden="true" />
											<FormattedMessage
												{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_BUTTON_LABEL_TEXT_TRANSLATION_DELETE} />
										</Button>
									</FormGroup>
								}
							</>
						</Collapse>
					</>
				}
				{!activeTranslation &&
					<div className="missing-translation-msg">
						<p className="text-center">
							<FormattedMessage
								{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_HELPER_TEXT_TRANSLATION_ADD}
								values={{
									language: LANGUAGE_CODES[formData.language].name,
								}} />
						</p>
						<Button
							color="neutral"
							outline
							onClick={actions.addTranslation}>
							<FormattedMessage
								{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_BUTTON_LABEL_TEXT_TRANSLATION_ADD} />
						</Button>
					</div>
				}
				<hr />
				<FormField
					id={`language-select-field-${id}`}
					type={FIELD_TYPES.SELECT}
					name={RESOURCE_FORM_FIELDS.LANGUAGE}
					value={formData.language}
					options={languageOptions}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FIELD_LABEL_TEXT_LANGUAGE)}
					isEditing
					onChange={actions.updateLanguage} />
				<FormField
					id={`width-select-field-${id}`}
					className="tc-form-group-width"
					type={FIELD_TYPES.SELECT}
					name={RESOURCE_FORM_FIELDS.WIDTH}
					value={String(formData.width)}
					options={WIDTH_OPTIONS}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FIELD_LABEL_TEXT_WIDTH)}
					isEditing
					onChange={actions.updateField} />
				<FormGroup>
					<Button
						className="me-3"
						type="submit"
						disabled={isSubmitting}
						color="primary">
						{isSubmitting
							? <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL_ACTIVE} />
							: <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
						}
					</Button>
					<Button
						type="button"
						color="neutral"
						outline
						onClick={onCancel}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
					</Button>
				</FormGroup>
			</Form>
			<DeleteConfirmationModal
				id={`tc-resource-${id}-translation-delete-confirmation-modal`}
				className="tc-resource-translation-delete-confirmation-modal"
				// eslint-disable-next-line max-len
				headerLabel={messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DELETE_CONFIRMATION_HEADER_TRANSLATION}
				// eslint-disable-next-line max-len
				body={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DELETE_CONFIRMATION_BODY_TRANSLATION)}
				submitLabel={messages.TC_COMPONENTS_GENERAL_TEXT_YES}
				requireConfirmation={false}
				showWarningAlert={false}
				size="lg"
				isOpen={showDeleteConfirmationModal}
				onCancel={() => setShowDeleteConfirmationModal(false)}
				onSubmit={handleDeleteTranslation} />
		</>
	);

};

ContentEditor.propTypes = propTypes;

export default ContentEditor;
